import { useEffect, useRef, useState } from 'preact/hooks';

/** Ниже этого сдвиг считается дрожанием пальца, а не жестом. */
const MIN_SHIFT = 8;
/** Насколько палец может сместиться, чтобы касание всё ещё считалось нажатием. */
const TAP_SLOP = 12;

/**
 * Свайп влево, открывающий действие под строкой.
 *
 * Отдельный хук, а не ветка в жестах сообщения: там свайп вправо срабатывает
 * по отпусканию и ничего не оставляет на экране, здесь строка остаётся
 * отъехавшей, пока действие не выбрали или не отменили. Разное поведение
 * после отпускания — разные хуки.
 *
 * Открытая строка не должна открывать чат по нажатию: палец в этот момент
 * целится в кнопку, а не в переписку. Поэтому наружу отдаётся ещё и
 * shouldSuppressClick — щелчок после жеста надо гасить, иначе браузер
 * пришлёт его следом за касанием и чат откроется поверх.
 */
export function useSwipeToReveal(width: number, revealed: boolean, onReveal: (open: boolean) => void) {
  const [shift, setShift] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const direction = useRef<'unknown' | 'horizontal' | 'vertical'>('unknown');
  const moved = useRef(false);

  /**
   * Сдвиг дублируется в ref: состояние обновляется не сразу, и обработчик
   * отпускания, читая только его, видел бы значение до последнего движения.
   */
  const current = useRef(0);
  const move = (value: number) => {
    current.current = value;
    setShift(value);
  };

  // Закрытие приходит и снаружи — когда открыли соседнюю строку. Держим
  // сдвиг в согласии с этим признаком, иначе строка останется отъехавшей.
  useEffect(() => {
    if (!revealed && current.current !== 0) move(0);
    if (revealed && current.current === 0) move(-width);
  }, [revealed, width]);

  const onTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;

    start.current = { x: touch.clientX, y: touch.clientY };
    direction.current = 'unknown';
    moved.current = false;
  };

  const onTouchMove = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch || !start.current) return;

    const dx = touch.clientX - start.current.x;
    const dy = touch.clientY - start.current.y;

    if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) moved.current = true;

    if (direction.current === 'unknown') {
      if (Math.abs(dx) < MIN_SHIFT && Math.abs(dy) < MIN_SHIFT) return;
      // Вертикаль отдаём списку: иначе прокрутка начнёт цеплять строки.
      direction.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
    }
    if (direction.current === 'vertical') return;

    const base = revealed ? -width : 0;
    // Сопротивление за пределом: дальше кнопки строка почти не едет —
    // так виден предел, и жест не превращается в перетаскивание.
    const next = base + dx;
    move(next > 0 ? next * 0.2 : Math.max(-width - 24, next));
  };

  const onTouchEnd = () => {
    const settled = current.current <= -width / 2;

    start.current = null;
    direction.current = 'unknown';

    move(settled ? -width : 0);
    if (settled !== revealed) onReveal(settled);
  };

  const onTouchCancel = () => {
    start.current = null;
    direction.current = 'unknown';
    move(revealed ? -width : 0);
  };

  return {
    shift,
    /** Был ли это жест, а не нажатие: щелчок после такого надо погасить. */
    shouldSuppressClick: () => moved.current || revealed,
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
  };
}
